import { configureStore } from '@reduxjs/toolkit';
import * as jose from 'jose';
import { authReducer } from './auth';
import { helloWorldReducer } from './hello-world/hello-world-slice';
import { loadingReducer } from './loading';
import { modalContainerReducer } from './modal-container';
import { navigationReducer } from './navigation';
import { toastAlertReducer } from './toast-alert';
import { userReducer } from './user';

// Lê o token do localStorage ANTES do primeiro render do React
// Isso elimina o race condition onde PrivateRoute avalia isValid=false
// antes do setupSession() restaurar o token via useEffect
const getPreloadedAuthState = () => {
  const timestampStorage = localStorage.getItem('LOGIN_TIMESTAMP');
  const loginTimestamp = timestampStorage ? Number(timestampStorage) : null;
  const sessionTimeoutSeconds = Number(process.env.SESSION_TIMEOUT_SECONDS);

  // Se a URL indica um logout/erro de sessão vindo de sistema externo (ex: VelesGEO),
  // limpa o token AGORA — antes do React montar — para que isValid parta como false.
  // Isso é a única forma definitiva de evitar race conditions com useEffects.
  const params = new URLSearchParams(window.location.search);
  const isLogoutUrl = params.get('logout') === 'true' || !!params.get('message') || !!params.get('reason');
  if (isLogoutUrl) {
    localStorage.removeItem('ACCESS_TOKEN');
    localStorage.removeItem('LOGIN_TIMESTAMP');
    return { isValid: false, token: '', user: null, sessionTimeoutSeconds, loginTimestamp: null };
  }

  try {
    const token = localStorage.getItem('ACCESS_TOKEN');
    if (token) {
      // Valida que o token é decodificável e não expirado
      const decoded = jose.decodeJwt(token);
      const currentTime = Math.floor(Date.now() / 1000);

      if (decoded.exp && decoded.exp > currentTime) {
        return { isValid: true, token, user: null, sessionTimeoutSeconds, loginTimestamp };
      } else {
        localStorage.removeItem('ACCESS_TOKEN');
        localStorage.removeItem('LOGIN_TIMESTAMP');
      }
    }
  } catch {
    localStorage.removeItem('ACCESS_TOKEN');
    localStorage.removeItem('LOGIN_TIMESTAMP');
  }
  return { isValid: false, token: '', user: null, sessionTimeoutSeconds, loginTimestamp: null };
};

// Pré-carrega informações do usuário e módulos do localStorage para renderização instantânea
const getPreloadedUserState = () => {
  try {
    const userInfo = localStorage.getItem('USER_INFO');
    const userModules = localStorage.getItem('USER_MODULES');
    return {
      currentUser: userInfo ? JSON.parse(userInfo) : undefined,
      modules: userModules ? JSON.parse(userModules) : []
    };
  } catch {
    return {
      currentUser: undefined,
      modules: []
    };
  }
};

export const store = configureStore({
  reducer: {
    auth: authReducer,
    loading: loadingReducer,
    navigation: navigationReducer,
    modalContainer: modalContainerReducer,
    toastAlert: toastAlertReducer,
    helloWorld: helloWorldReducer,
    user: userReducer
  },
  preloadedState: {
    auth: getPreloadedAuthState(),
    user: getPreloadedUserState()
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
    }),
});

export type AppDispatch = typeof store.dispatch;
export type RootState = ReturnType<typeof store.getState>;